import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import type { User } from '@wml-crm/shared';
import { setToken } from '../api/client.js';
import { SelfPasswordModal } from './SelfPasswordModal.js';

interface Props {
  user: User;
}

function firstLetter(s: string): string {
  const c = s.trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

export function Sidebar({ user }: Props) {
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <aside className="sidebar">
      <Link to="/" className="sidebar-brand" title="WML Software · на главную">
        <div className="brand-logo-full" role="img" aria-label="WML Software" />
      </Link>

      <div className="nav-section">Панель</div>
      <NavLink to="/" end className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
        </svg>
        Поиск моделей
      </NavLink>
      <NavLink to="/models" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
        Все модели
      </NavLink>
      <NavLink to="/top-posts" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 20V10" />
          <path d="M9 20V4" />
          <path d="M15 20V13" />
          <path d="M21 20V8" />
        </svg>
        Топ 200 постов
      </NavLink>
      <NavLink to="/top-day" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4" />
          <path d="M8 2v4" />
          <path d="M3 10h18" />
        </svg>
        Топ 50 за день
      </NavLink>
      <NavLink to="/top-tags" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.42 0l8.58-8.58a1 1 0 0 0 0-1.42Z" />
          <circle cx="7" cy="7" r="1.5" />
        </svg>
        Топ 100 по типажам
      </NavLink>
      {user.canViewAnalyzed && (
        <NavLink to="/analyzed" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          Проанализированные модели
        </NavLink>
      )}

      {user.role === 'admin' && (
        <>
          <div className="nav-section">Управление</div>
          <NavLink to="/admin" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l3 6 6.5 1-4.75 4.75L18 21l-6-3.25L6 21l1.25-7.25L2.5 9 9 8z" />
            </svg>
            Админка
          </NavLink>
        </>
      )}

      <div className="sidebar-footer">
        <div className="avatar">{firstLetter(user.name)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="user-name">{user.name}</div>
          <div className="user-email">{user.email}</div>
        </div>
        <button
          type="button"
          className="sidebar-pw-btn"
          title="Сменить пароль"
          onClick={() => setPwOpen(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="15" r="4" />
            <path d="m10.85 12.15 8.15-8.15" />
            <path d="m18 5 3 3" />
            <path d="m15 8 3 3" />
          </svg>
        </button>
      </div>

      {pwOpen && (
        <SelfPasswordModal
          onClose={() => setPwOpen(false)}
          onChanged={(nextToken) => setToken(nextToken)}
        />
      )}
    </aside>
  );
}
